import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowForwardRounded,
  RefreshRounded,
  WarningAmberRounded,
  ScheduleRounded,
  NotificationsActiveOutlined,
  BuildOutlined,
  DirectionsCarOutlined,
  BadgeOutlined,
  FilterListRounded,
  DoneRounded,
} from "@mui/icons-material";
import { SkeletonStatus, SkeletonText } from "Components/Dashboard/Skeleton";
import {
  REMINDERS_SERVICES,
  REMINDERS_VEHICLES,
  REMINDERS_CONTACTS,
} from "Shared/Constants/routes";
import { ReminderType, reminderTypeLabels } from "./constants";
import { useReminderTable } from "./useReminderTable";
import {
  prioritizeReminders,
  normalizeReminderStatus,
  reminderDetailPath,
  reminderDueLabel,
  reminderStatusLabel,
} from "./reminderAttention";
import styles from "./styles/ReminderTable.module.scss";

type Urgency = "all" | "overdue" | "due_soon";
const typeIcons = {
  service_reminder: BuildOutlined,
  vehicle_renewal: DirectionsCarOutlined,
  contact_renewal: BadgeOutlined,
};
const VISIBLE_ROWS = 5;

// Same markup as a full loaded preview (including "Show N loaded"), so the
// list keeps its height on arrival.
const ReminderRowsSkeleton = () => (
  <div aria-hidden="true">
    <ul className={styles.list}>
      {Array.from({ length: VISIBLE_ROWS }, (_, index) => (
        <li key={index} className={styles.row}>
          <span className={styles.recordIcon} />
          <span className={styles.record}>
            <strong>
              <SkeletonText width="9em" />
            </strong>
            <span>
              <SkeletonText width="7em" />
            </span>
          </span>
          <span className={styles.status}>
            <SkeletonText width="5em" />
          </span>
          <span className={styles.date}>
            <SkeletonText width="8em" />
          </span>
          <span className={styles.action} />
        </li>
      ))}
    </ul>
    <div className={styles.previewFooter}>
      <span>
        <SkeletonText width="25em" />
      </span>
      <button type="button" tabIndex={-1} disabled>
        <SkeletonText width="6em" />
      </button>
    </div>
  </div>
);

export const ReminderTable: FC = () => {
  const [selectedType, setSelectedType] = useState<ReminderType | "">("");
  const [urgency, setUrgency] = useState<Urgency>("all");
  const [expanded, setExpanded] = useState(false);
  const {
    dashboardReminders: data,
    loading,
    error,
    retry,
  } = useReminderTable(selectedType);
  const rows = prioritizeReminders(data?.results || []);
  const overdue = rows.filter(
    (item) => normalizeReminderStatus(item.status) === "overdue",
  ).length;
  const dueSoon = rows.filter(
    (item) => normalizeReminderStatus(item.status) === "due_soon",
  ).length;
  const filtered =
    urgency === "all"
      ? rows
      : rows.filter((item) => normalizeReminderStatus(item.status) === urgency);
  const visible = expanded ? filtered : filtered.slice(0, VISIBLE_ROWS);
  const chooseType = (value: ReminderType | "") => {
    setSelectedType(value);
    setExpanded(false);
  };
  const chooseUrgency = (value: Urgency) => {
    setUrgency(value);
    setExpanded(false);
  };
  const metrics = [
    {
      value: "overdue" as Urgency,
      count: overdue,
      label: "Overdue",
      icon: WarningAmberRounded,
    },
    {
      value: "due_soon" as Urgency,
      count: dueSoon,
      label: "Due soon",
      icon: ScheduleRounded,
    },
    {
      value: "all" as Urgency,
      count: rows.length,
      label: "All loaded",
      icon: NotificationsActiveOutlined,
    },
  ];

  return (
    <section
      className={styles.attention}
      aria-busy={loading}
      aria-labelledby="reminder-heading"
    >
      <div className={styles.heading}>
        <div className={styles.title}>
          <NotificationsActiveOutlined aria-hidden="true" />
          <h2 id="reminder-heading">Needs attention</h2>
        </div>
        {!error && (
          <span className={styles.scope}>
            {loading ? (
              <SkeletonText width="16em" />
            ) : (
              <>
                Priority preview · {rows.length} loaded of{" "}
                {data?.count.toLocaleString() ?? "0"}
              </>
            )}
          </span>
        )}
      </div>
      {!error && (
        <div
          className={styles.metrics}
          role="group"
          aria-label="Filter loaded reminders by urgency"
        >
          {metrics.map(({ value, count, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              className={`${styles.metric} ${styles[value]}`}
              aria-label={loading ? label : `${count} ${label.toLowerCase()}`}
              aria-pressed={urgency === value}
              disabled={loading}
              onClick={() => chooseUrgency(value)}
            >
              <span className={styles.metricTop}>
                <Icon aria-hidden="true" />
                <span>{label}</span>
                <DoneRounded
                  className={styles.selectedIcon}
                  aria-hidden="true"
                />
              </span>
              <strong key={count} className={styles.metricNumber}>
                {loading ? (
                  <SkeletonText width="1.2em" />
                ) : (
                  count.toLocaleString()
                )}
              </strong>
              <span className={styles.metricBottom}>
                View reminders <ArrowForwardRounded aria-hidden="true" />
              </span>
            </button>
          ))}
        </div>
      )}
      <div className={styles.filterLine}>
        <div
          className={styles.filters}
          role="group"
          aria-label="Filter reminders by type"
        >
          <button
            type="button"
            aria-pressed={!selectedType}
            onClick={() => chooseType("")}
          >
            <FilterListRounded aria-hidden="true" />
            All types
          </button>
          {(Object.keys(reminderTypeLabels) as ReminderType[]).map((type) => {
            const count =
              !loading && !error
                ? data?.reminders_count.find((item) => item.type === type)
                    ?.count
                : undefined;
            const Icon = typeIcons[type];
            return (
              <button
                type="button"
                key={type}
                aria-pressed={selectedType === type}
                onClick={() => chooseType(type)}
              >
                <Icon aria-hidden="true" />
                {reminderTypeLabels[type]}
                {/* Always rendered, so the chip keeps its width when counts arrive. */}
                <span
                  className={
                    !loading && count === undefined ? styles.noCount : undefined
                  }
                >
                  {loading ? (
                    <SkeletonText width="3ch" />
                  ) : (
                    count?.toLocaleString()
                  )}
                </span>
              </button>
            );
          })}
        </div>
        <span className={styles.filterHint}>
          Type totals include all statuses
        </span>
      </div>
      {loading ? (
        <>
          <SkeletonStatus>Loading priority reminders…</SkeletonStatus>
          <ReminderRowsSkeleton />
        </>
      ) : error ? (
        <div className={styles.message} role="alert">
          <p>
            Reminders could not be loaded. Their current status is unavailable.
          </p>
          <button type="button" onClick={retry}>
            <RefreshRounded aria-hidden="true" /> Try again
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p className={styles.message}>
          No reminders found
          {selectedType
            ? ` for ${reminderTypeLabels[selectedType].toLowerCase()}`
            : ""}
          .
        </p>
      ) : filtered.length === 0 ? (
        <div className={styles.message} role="status">
          <p>
            No {urgency === "overdue" ? "overdue" : "due soon"} reminders in
            this loaded preview.
          </p>
          <button type="button" onClick={() => chooseUrgency("all")}>
            Show all loaded reminders
          </button>
        </div>
      ) : (
        <>
          <ul className={styles.list} aria-label="Priority reminders">
            {visible.map((item) => {
              const Icon = typeIcons[item.type];
              return (
                <li key={`${item.type}-${item.id}`} className={styles.row}>
                  <span className={styles.recordIcon}>
                    <Icon aria-hidden="true" />
                  </span>
                  <Link className={styles.record} to={reminderDetailPath(item)}>
                    <strong>
                      {item.associated_item?.trim() || "Item unavailable"}
                    </strong>
                    <span>{reminderTypeLabels[item.type]}</span>
                  </Link>
                  <span
                    className={`${styles.status} ${styles[normalizeReminderStatus(item.status)] || styles.other}`}
                  >
                    <span className={styles.statusDot} />
                    {reminderStatusLabel(item.status)}
                  </span>
                  <span className={styles.date}>
                    {item.date ? "Due " : ""}
                    {reminderDueLabel(item.date)}
                  </span>
                  <Link
                    className={styles.action}
                    to={reminderDetailPath(item)}
                    aria-label={`Review ${reminderTypeLabels[item.type].toLowerCase()} for ${item.associated_item?.trim() || "unavailable item"}`}
                  >
                    <ArrowForwardRounded aria-hidden="true" />
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className={styles.previewFooter}>
            <span aria-live="polite">
              {urgency === "all"
                ? `Showing ${visible.length} of ${data?.count.toLocaleString()} matching reminders · overdue first`
                : `Showing ${visible.length} of ${filtered.length} ${urgency === "overdue" ? "overdue" : "due soon"} in loaded preview`}
            </span>
            {filtered.length > VISIBLE_ROWS && (
              <button type="button" onClick={() => setExpanded(!expanded)}>
                {expanded ? "Show fewer" : `Show ${filtered.length} loaded`}
              </button>
            )}
          </div>
        </>
      )}
      <nav className={styles.footer} aria-label="All reminder lists">
        <span>Full lists</span>
        <Link to={REMINDERS_SERVICES}>
          Service <ArrowForwardRounded aria-hidden="true" />
        </Link>
        <Link to={REMINDERS_VEHICLES}>
          Vehicles <ArrowForwardRounded aria-hidden="true" />
        </Link>
        <Link to={REMINDERS_CONTACTS}>
          Contacts <ArrowForwardRounded aria-hidden="true" />
        </Link>
      </nav>
    </section>
  );
};
